import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  BookOpen, Search, Flame, Sparkles, Settings2, ArrowRight,
  Bookmark, Focus, Volume2, Type, Menu, Bell, X, Brain,
  CalendarCheck, Sun, Moon, Languages, Sprout,
  Headphones, UsersRound, HeartHandshake, NotebookPen, Users, Crown, Compass,
} from "lucide-react";
import { searchBible } from "../api";
import { useSettings } from "../context/SettingsContext";
import {
  getLast, getStreak, readCount, getBookmarks, getMemoryStats,
} from "../lib/storage";
import { notificationPermission, enableReminders } from "../lib/reminders";
import AmbientBackground from "../components/AmbientBackground";
import FocusJourneyScene from "../components/FocusJourneyScene";
import BookPicker from "../components/BookPicker";
import SettingsSheet from "../components/SettingsSheet";
import AppMenu, { TOOLS } from "../components/AppMenu";
import TranslationToggle from "../components/TranslationToggle";
import ReadingHistory from "../components/ReadingHistory";
import GoalDialog from "../components/GoalDialog";
import VerseOfDayCard from "../components/VerseOfDayCard";
import { goalProgress } from "../lib/goal";
import confetti from "canvas-confetti";
import Onboarding from "../components/Onboarding";
import { isAppBilling } from "../lib/playBilling";
import { tourActive } from "../lib/tour";
import { track } from "../lib/analytics";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { Input } from "../components/ui/input";

const FEATURES = [
  { icon: Languages, bg: "/cards/translation.jpg", title: "Word Study", text: "See what a word truly meant in the original Hebrew, Greek & Aramaic — with key verses.", to: "/word-study" },
  { icon: Compass, bg: "/cards/plans.jpg", title: "Reading Journey", text: "See your history and Bible coverage — every book you've read, at a glance.", to: "/journey" },
  { icon: Sun, bg: "/cards/devotionals.jpg", title: "Daily Devotionals", text: "Genuine Spurgeon readings by theme, each with a fresh real-life story and art.", to: "/devotionals" },
  { icon: CalendarCheck, bg: "/cards/plans.jpg", title: "Reading Plans", text: "Topical journeys and Bible-in-a-year — they guide you to the next chapter.", to: "/plans" },
  { icon: Sparkles, bg: "/cards/votd.jpg", title: "Verse of the Day", text: "A beautiful, shareable verse every morning — tuned to the season & holidays.", to: "/verse" },
  { icon: Bookmark, bg: "/cards/stat-saved.jpg", title: "Saved Verses", text: "Every verse you bookmark, in one place — revisit, share, or remove any time.", to: "/saved" },
  { icon: Headphones, bg: "/cards/audio.jpg", title: "Audio Bible", text: "Listen and follow along with gentle narration, hands-free.", to: "/listen" },
  { icon: UsersRound, bg: "/cards/characters.jpg", title: "Character Cards", text: "Meet the people of Scripture with fast, beautiful profile cards.", to: "/characters" },
  { icon: Brain, bg: "/cards/memorize.jpg", title: "Scripture Memory", text: "Hide God's word in your heart with popular verses and spaced practice.", to: "/memorize" },
  { icon: HeartHandshake, bg: "/cards/prayer.jpg", title: "Prayer Journal", text: "Track your prayers and celebrate the ones God answers.", to: "/journal" },
  { icon: NotebookPen, bg: "/cards/notes.jpg", title: "Sermon Notes", text: "Capture every message — speaker, passage, and takeaways.", to: "/notes" },
  { icon: Users, bg: "/cards/family.jpg", title: "Family Sharing", text: "Pray together with a private family group and shared prayer wall.", to: "/family" },
  { icon: Focus, bg: "/cards/focus.jpg", title: "Focus Mode", text: "One verse glows at a time while the rest gently fade — built for ADHD minds.", to: "/read/JHN/1" },
  { icon: Languages, bg: "/cards/translation.jpg", title: "Your Translation", text: "Read and memorize in WEB, KJV, NKJV, NLT or ASV — switch any time.", to: "/read/PSA/23" },
  { icon: Crown, bg: "/cards/premium.jpg", title: "Clarity Bible Premium", text: "Unlock everything and support the mission of Clarity Bible.", to: "/premium" },
];

function SearchDialog({ open, onOpenChange }) {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (q.trim().length < 2) {
      setResults([]);
      return;
    }
    setLoading(true);
    const t = setTimeout(() => {
      searchBible(q.trim()).then((r) => {
        setResults(r);
        setLoading(false);
      });
    }, 350);
    return () => clearTimeout(t);
  }, [q]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl gap-0 overflow-hidden p-0" data-testid="search-dialog">
        <DialogTitle className="sr-only">Search the Bible</DialogTitle>
        <DialogDescription className="sr-only">Search verses across the World English Bible</DialogDescription>
        <div className="flex items-center gap-3 border-b border-border px-5 py-4">
          <Search className="h-5 w-5 text-muted-foreground" />
          <Input
            autoFocus
            data-testid="search-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search the Word — e.g. ‘peace’, ‘light’, ‘love one another’"
            className="font-ui border-0 bg-transparent text-base shadow-none focus-visible:ring-0"
          />
        </div>
        <div className="max-h-[60vh] overflow-y-auto">
          {loading && <p className="font-ui p-6 text-sm text-muted-foreground">Searching…</p>}
          {!loading && q.length >= 2 && results.length === 0 && (
            <p className="font-ui p-6 text-sm text-muted-foreground">No verses found.</p>
          )}
          {results.map((r, i) => (
            <button
              key={i}
              data-testid={`search-result-${i}`}
              onClick={() => {
                onOpenChange(false);
                navigate(`/read/${r.book}/${r.chapter}`);
              }}
              className="block w-full border-b border-border px-5 py-4 text-left transition-colors hover:bg-muted"
            >
              <p className="font-ui mb-1 text-xs font-semibold uppercase tracking-wider text-primary">
                {r.book_name} {r.chapter}:{r.verse}
              </p>
              <p className="reading-text text-sm leading-relaxed text-foreground/90 line-clamp-2">
                {r.text}
              </p>
            </button>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function Home() {
  const navigate = useNavigate();
  const [pickerOpen, setPickerOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const { theme, toggleTheme } = useSettings();
  const [searchOpen, setSearchOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [goalOpen, setGoalOpen] = useState(false);
  const [goalTick, setGoalTick] = useState(0);
  const [showRemind, setShowRemind] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(() => {
    const force = new URLSearchParams(window.location.search).get("intro") === "1";
    if (force) return true;
    // The guided tour is the first-run experience inside the app — don't also
    // show the swipe intro there, or while a tour is active/forced.
    const forceTour = localStorage.getItem("cb_force_tour") === "1" || localStorage.getItem("force_tour") === "1";
    if (isAppBilling() || tourActive() || forceTour) return false;
    return !localStorage.getItem("fw_onboarded");
  });

  const last = getLast();
  const streak = getStreak();
  const chaptersRead = readCount();
  const bookmarks = getBookmarks();
  const memory = getMemoryStats();

  const goal = goalProgress();
  const journeyProgress = goal.pct;
  const phase =
    journeyProgress >= 1 ? "In full bloom" :
    journeyProgress >= 0.6 ? "Morning bloom" :
    journeyProgress >= 0.3 ? "Dawn is breaking" :
    "Planting the seed";

  // Celebrate once per day when the Focus Journey tree reaches full bloom.
  const [bloomCelebrate, setBloomCelebrate] = useState(false);
  useEffect(() => {
    if (journeyProgress < 1) return;
    const today = new Date().toDateString();
    if (localStorage.getItem("fw_bloom_celebrated") === today) return;
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) {
      localStorage.setItem("fw_bloom_celebrated", today);
      return;
    }
    const t = setTimeout(() => {
      localStorage.setItem("fw_bloom_celebrated", today);
      setBloomCelebrate(true);
      const colors = ["#F2C14E", "#E0B85C", "#5C8A6E", "#FDEBD0", "#FFF3D0"];
      const fire = (ratio, opts) =>
        confetti({ ...opts, origin: { y: 0.62 }, colors, particleCount: Math.floor(200 * ratio) });
      fire(0.25, { spread: 26, startVelocity: 55 });
      fire(0.2, { spread: 60 });
      fire(0.35, { spread: 100, decay: 0.91, scalar: 0.8 });
      fire(0.1, { spread: 120, startVelocity: 25, decay: 0.92, scalar: 1.2 });
      fire(0.1, { spread: 120, startVelocity: 45 });
      setTimeout(() => setBloomCelebrate(false), 4200);
    }, 1400); // let the tree finish blooming first
    return () => clearTimeout(t);
  }, [journeyProgress]);

  const greeting = (() => {
    const h = new Date().getHours();
    return h < 12 ? "Good morning" : h < 18 ? "Good afternoon" : "Good evening";
  })();

  useEffect(() => {
    if (
      notificationPermission() === "default" &&
      chaptersRead > 0 &&
      !localStorage.getItem("fw_remind_dismissed")
    ) {
      setShowRemind(true);
    }
  }, [chaptersRead]);

  useEffect(() => {
    const imgs = [
      ...TOOLS.map((t) => t.bg),
      ...FEATURES.map((f) => f.bg),
      "/cards/votd.jpg", "/cards/premium.jpg", "/cards/translation.jpg",
      "/cards/stat-memory.jpg", "/cards/stat-saved.jpg", "/cards/stat-streak.jpg",
    ].filter(Boolean);
    imgs.forEach((src) => { const i = new Image(); i.src = src; });
  }, []);


  const dismissRemind = () => {
    localStorage.setItem("fw_remind_dismissed", "1");
    setShowRemind(false);
  };
  const acceptRemind = async () => {
    await enableReminders();
    dismissRemind();
  };

  return (
    <div className="relative min-h-screen">
      {showOnboarding && <Onboarding onDone={() => setShowOnboarding(false)} />}
      <AmbientBackground theme={{ a: "#4C6FE0", b: "#8A6FD1", c: "#E8A07A" }} />

      {/* top bar */}
      <header className="font-ui mx-auto flex max-w-6xl items-center justify-between px-6 py-6">
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <img
              src="/logo_mark.png"
              alt="Clarity Bible logo"
              data-testid="home-logo"
              className="h-10 w-10 object-contain"
            />
            <span className="flex flex-col leading-none">
              <span className="font-serif-display text-xl font-semibold tracking-tight">Clarity Bible</span>
              <span className="font-ui mt-1 text-[0.65rem] font-medium uppercase tracking-[0.18em] text-primary/80">Scripture that holds your attention</span>
            </span>
          </div>
          <TranslationToggle className="hidden sm:inline-flex" />
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={toggleTheme}
            data-testid="theme-toggle-header"
            aria-label="Toggle light or dark mode"
            className="flex h-10 w-10 items-center justify-center rounded-full border border-border bg-background/60 backdrop-blur-md transition-colors hover:bg-muted"
          >
            {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
          </button>
          <button
            onClick={() => setSearchOpen(true)}
            data-testid="open-search"
            className="flex h-10 w-10 items-center justify-center rounded-full border border-border bg-background/60 backdrop-blur-md transition-colors hover:bg-muted"
          >
            <Search className="h-4 w-4" />
          </button>
          <button
            onClick={() => setSettingsOpen(true)}
            data-testid="home-settings"
            className="flex h-10 w-10 items-center justify-center rounded-full border border-border bg-background/60 backdrop-blur-md transition-colors hover:bg-muted"
          >
            <Settings2 className="h-4 w-4" />
          </button>
          <button
            onClick={() => setMenuOpen(true)}
            data-testid="open-menu"
            className="flex h-10 items-center gap-2 rounded-full border border-border bg-background/60 px-4 backdrop-blur-md transition-colors hover:bg-muted"
          >
            <Menu className="h-4 w-4" />
            <span className="hidden text-sm font-medium sm:inline">Tools</span>
          </button>
        </div>
      </header>

      {/* reading reminder prompt */}
      {showRemind && (
        <div className="mx-auto max-w-6xl px-6" data-testid="reminder-banner">
          <div className="font-ui flex items-center gap-3 rounded-2xl border border-primary/40 bg-primary/10 px-5 py-3.5 backdrop-blur-md">
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-primary/20 text-primary">
              <Bell className="h-4 w-4" />
            </span>
            <p className="flex-1 text-sm text-foreground/90">
              Want a gentle nudge if you haven’t read in 24 hours?
            </p>
            <button
              onClick={acceptRemind}
              data-testid="reminder-enable"
              className="rounded-full bg-primary px-4 py-2 text-xs font-semibold text-primary-foreground transition-transform hover:scale-[1.03]"
            >
              Enable
            </button>
            <button
              onClick={dismissRemind}
              data-testid="reminder-dismiss"
              className="flex h-10 w-10 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-muted"
            >
              <X className="h-6 w-6" />
            </button>
          </div>
        </div>
      )}

      {/* Continue reading · Select a book · Translation — one grouped panel */}
      <section className="mx-auto mt-2 max-w-4xl px-4 sm:px-6">
        <div className="overflow-hidden rounded-2xl border border-border bg-card divide-y divide-border">
          <button
            onClick={() => (last ? navigate(`/read/${last.book}/${last.chapter}`) : navigate("/read/JHN/1"))}
            data-testid="continue-reading-top"
            className="group flex w-full items-center justify-between gap-4 bg-primary/10 px-5 py-4 text-left transition-colors hover:bg-primary/[0.16]"
          >
            <span className="flex min-w-0 items-center gap-3">
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-primary/15 text-primary">
                <BookOpen className="h-5 w-5" />
              </span>
              <span className="min-w-0">
                <span className="font-ui block text-xs font-semibold uppercase tracking-[0.2em] text-primary">
                  {last ? "Continue reading" : "Start reading"}
                </span>
                <span className="font-serif-display block truncate text-lg leading-tight text-foreground">
                  {last ? `${last.bookName || last.book} ${last.chapter}` : "John 1"}
                </span>
              </span>
            </span>
            <ArrowRight className="h-5 w-5 shrink-0 text-primary transition-transform group-hover:translate-x-1" />
          </button>
          <button
            onClick={() => setPickerOpen(true)}
            data-testid="browse-books"
            className="font-ui flex w-full items-center justify-center gap-2 py-4 text-sm font-semibold text-foreground transition-colors hover:bg-secondary"
          >
            <BookOpen className="h-4 w-4 text-primary" /> Select a book
          </button>
          <div
            data-testid="landing-translation"
            className="flex flex-wrap items-center justify-center gap-x-3 gap-y-2 px-4 py-4"
          >
            <span className="font-ui text-xs font-semibold uppercase tracking-[0.15em] text-muted-foreground">
              Your translation
            </span>
            <TranslationToggle />
          </div>
        </div>
      </section>

      {/* verse of the day */}
      <section className="mx-auto mt-2 max-w-4xl px-4 sm:px-6">
        <VerseOfDayCard />
      </section>

      {/* Focus Journey */}
      <section className="mx-auto mt-2 max-w-4xl px-4 sm:px-6">
        <div className="relative">
          <FocusJourneyScene progress={journeyProgress} />
          {bloomCelebrate && (
            <motion.div
              data-testid="bloom-celebration"
              initial={{ opacity: 0, y: -12, scale: 0.92 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0 }}
              className="pointer-events-none absolute left-1/2 top-5 z-10 -translate-x-1/2 rounded-full border border-[#FDEBD0]/40 bg-[#2E1F12]/70 px-5 py-2.5 backdrop-blur-md"
            >
              <span className="font-ui flex items-center gap-2 text-sm font-bold text-[#FDEBD0]">
                <Sparkles className="h-4 w-4" /> In full bloom — you stayed in the Word today!
              </span>
            </motion.div>
          )}
          <div className="pointer-events-none absolute inset-x-0 bottom-0 p-6 sm:p-8">
            <div className="pointer-events-auto flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
              <div className="max-w-sm">
                <span className="font-ui inline-flex items-center gap-1.5 rounded-full bg-[#FDEBD0]/15 px-3 py-1 text-[0.65rem] font-semibold uppercase tracking-[0.2em] text-[#FDEBD0] backdrop-blur-sm">
                  <Sprout className="h-3.5 w-3.5" /> Your Focus Journey
                </span>
                <p data-testid="progress-summary-text" className="font-serif-display mt-3 text-3xl leading-tight text-[#FDEBD0] sm:text-4xl">
                  {phase}
                </p>
                <p data-testid="journey-explainer" className="font-ui mt-2 text-sm leading-snug text-[#FDEBD0]/90">
                  This tree grows every time you read. Each chapter takes it from seed to full bloom — a living picture of your time in God's Word.
                </p>
                <p className="font-ui mt-2 text-sm font-semibold text-[#FDEBD0]/95">
                  {goal.detail}
                </p>
                <div className="mt-3 h-2 max-w-xs overflow-hidden rounded-full bg-[#FDEBD0]/20">
                  <div
                    className="h-full rounded-full bg-[#FDEBD0] transition-all duration-700"
                    style={{ width: `${Math.round((goal.pct || 0) * 100)}%` }}
                    data-testid="goal-progress-bar"
                  />
                </div>
                <button
                  onClick={() => setGoalOpen(true)}
                  data-testid="set-goal-btn"
                  className="font-ui mt-5 inline-flex items-center gap-2 rounded-full bg-[#FDEBD0] px-7 py-3.5 text-sm font-bold text-[#2E1F12] shadow-lg shadow-black/30 transition-transform hover:scale-[1.04]"
                >
                  <Settings2 className="h-4 w-4" /> {(goal.pct || 0) > 0 ? "Change goal or plan" : "Set a goal or pick a plan"}
                </button>
              </div>
              {goal.mode === "plan" && goal.planId && (
                <button
                  onClick={() => navigate(`/plans?plan=${goal.planId}`)}
                  data-testid="journey-continue"
                  className="font-ui inline-flex items-center gap-2 self-start rounded-full bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground shadow-lg transition-transform hover:scale-[1.03] sm:self-auto"
                >
                  {goal.done > 0 ? "Continue plan" : "Open plan"} <ArrowRight className="h-4 w-4" />
                </button>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* hero — search & stats */}
      <section className="mx-auto max-w-5xl px-6 pb-10 pt-6 text-center">
        {/* prominent search */}
        <motion.button
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.1 }}
          onClick={() => setSearchOpen(true)}
          data-testid="hero-search"
          className="group relative mx-auto flex w-full max-w-xl items-center gap-3 overflow-hidden rounded-2xl border border-border px-5 py-4 text-left transition-all hover:border-primary/60"
        >
          <img src="/cards/votd.jpg" alt="" className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
          <div className="absolute inset-0 bg-gradient-to-r from-black/90 via-black/65 to-black/40" />
          <Search className="relative h-5 w-5 text-white" />
          <span className="relative text-sm text-white/90 drop-shadow sm:text-base">
            Search Scripture — try “peace”, “light”, or “love one another”
          </span>
        </motion.button>

        {/* stats — one cohesive panel with hairline dividers (no photos) */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.15 }}
          className="mx-auto mt-14 max-w-2xl overflow-hidden rounded-2xl border border-border"
        >
          <div className="grid grid-cols-2 gap-px bg-border sm:grid-cols-4">
            <div className="bg-card p-5 text-center" data-testid="stat-streak">
              <Flame className="mx-auto mb-2 h-5 w-5 text-primary" />
              <p className="font-serif-display text-3xl font-semibold text-foreground">{streak.count}</p>
              <p className="font-ui text-xs text-muted-foreground">Day Streak</p>
            </div>
            <button onClick={() => navigate("/journey")} className="bg-card p-5 text-center transition-colors hover:bg-secondary" data-testid="stat-chapters">
              <BookOpen className="mx-auto mb-2 h-5 w-5 text-primary" />
              <p className="font-serif-display text-3xl font-semibold text-foreground">{chaptersRead}</p>
              <p className="font-ui text-xs text-muted-foreground">Chapters Read</p>
            </button>
            <div className="bg-card p-5 text-center" data-testid="stat-memory">
              <Brain className="mx-auto mb-2 h-5 w-5 text-primary" />
              <p className="font-serif-display text-3xl font-semibold text-foreground">{memory.chapters > 0 ? `${memory.pct}%` : "—"}</p>
              <p className="font-ui text-xs text-muted-foreground">Memory Score</p>
            </div>
            <button onClick={() => navigate("/saved")} className="bg-card p-5 text-center transition-colors hover:bg-secondary" data-testid="stat-bookmarks">
              <Bookmark className="mx-auto mb-2 h-5 w-5 text-primary" />
              <p className="font-serif-display text-3xl font-semibold text-foreground">{bookmarks.length}</p>
              <p className="font-ui text-xs text-muted-foreground">Verses Saved</p>
            </button>
          </div>
        </motion.div>

        {/* Reading Journey — prominent entry */}
        <motion.button
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          onClick={() => navigate("/journey")}
          data-testid="reading-journey-card"
          className="group relative mx-auto mt-6 flex w-full max-w-2xl items-center gap-4 overflow-hidden rounded-2xl border border-primary/30 bg-primary/[0.07] p-5 text-left transition-all hover:border-primary/50 hover:bg-primary/[0.12]"
        >
          <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-primary/15 text-primary">
            <Compass className="h-6 w-6" />
          </span>
          <span className="min-w-0 flex-1">
            <span className="font-serif-display block text-lg leading-tight text-foreground">Your Reading Journey</span>
            <span className="font-ui block text-sm text-muted-foreground">See your history and watch the Bible light up as you read.</span>
          </span>
          <ArrowRight className="h-5 w-5 shrink-0 text-primary transition-transform group-hover:translate-x-1" />
        </motion.button>
      </section>

      {/* all features — one unified section */}
      <section className="mx-auto max-w-5xl px-6 py-12 pb-28">
        <h2 className="font-serif-display mb-3 text-center text-3xl tracking-tight md:text-4xl">
          Everything for your walk
        </h2>
        <p className="font-ui mx-auto mb-10 max-w-xl text-center text-sm text-muted-foreground">
          A focus-friendly Bible built to bring you back every day.
        </p>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {FEATURES.map((f, i) => (
            <motion.div
              key={f.title}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.5, delay: (i % 3) * 0.05 }}
            >
              <Link
                to={f.to}
                onClick={() => track("feature_open", { feature: f.title })}
                data-testid={`feature-${f.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                className="group flex h-full items-center gap-4 rounded-2xl border border-border bg-card p-5 transition-all duration-300 hover:border-primary/40 hover:bg-secondary"
              >
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary transition-transform duration-300 group-hover:scale-110">
                  <f.icon className="h-5 w-5" />
                </span>
                <div className="min-w-0">
                  <p className="font-ui text-base font-semibold text-foreground">{f.title}</p>
                  <p className="font-ui line-clamp-2 text-xs leading-snug text-muted-foreground">{f.text}</p>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </section>

      <footer className="font-ui border-t border-border/50 py-8 text-center text-xs text-muted-foreground">
        Clarity Bible · World English Bible (public domain) · Read in peace.
        <span className="mx-2">·</span>
        <Link to="/privacy" data-testid="privacy-link" className="hover:text-primary hover:underline">
          Privacy
        </Link>
      </footer>

      <BookPicker open={pickerOpen} onOpenChange={setPickerOpen} />
      <SettingsSheet open={settingsOpen} onOpenChange={setSettingsOpen} />
      <SearchDialog open={searchOpen} onOpenChange={setSearchOpen} />
      <AppMenu open={menuOpen} onOpenChange={setMenuOpen} />
      <ReadingHistory open={historyOpen} onOpenChange={setHistoryOpen} />
      <GoalDialog open={goalOpen} onOpenChange={setGoalOpen} onSaved={() => setGoalTick((t) => t + 1)} />
    </div>
  );
}
